<script setup>
import { ref, reactive, onMounted, computed } from "vue";
import { useTheme } from "vuetify";
import { appBarStore } from "@/stores/appBar";
import { apiQuery } from "@/api/j24api";
import AlertsList from "@/components/AlertsList.vue";
import LoadingStatus from "./components/LoadingStatus.vue";
import { patch } from "aws-amplify/api";
import DashDrillDown from "./components/DashDrilldown.vue";
import DashUpdatesPending from "./components/DashUpdatesPending.vue";
import LogDrillDown from "./components/LogDrillDown.vue";
import PackagesUpdated from "./components/PackagesUpdated.vue";
import AppIcon from "./components/AppIcon.vue";
import { storeToRefs } from "pinia";
import { patch24Store } from "@/stores/patch24";
const patch24 = patch24Store();
const { appsList } = storeToRefs(patch24);

const randomKeys = ref({
  logDrillDown: 0,
  dashDrillDown: 0,
  dashUpdatesPending: 0,
  packagesUpdated: 0,
});

function updateRandom(key) {
  randomKeys.value[key] = Math.floor(Math.random() * 100);
}

const theme = useTheme();
const dashboardData = ref({});
const data = ref({
  topRow: [
    {
      title: "Total Devices",
      icon: "mdi-laptop",
      count: 0,
      dataType: "devices",
    },
    {
      title: "Updates Installed",
      icon: "mdi-package-variant",
      count: 0,
      dataType: "packagesUpdated",
    },
    {
      title: "Updates Pending",
      icon: "mdi-package-variant-closed",
      count: 0,
      dataType: "updatesPending",
    },
    {
      title: "Updates Failed",
      icon: "mdi-alert-outline",
      count: 0,
      dataType: "updatesFailed",
    },
  ],
  macsOnline: [0],
  iosOnline: [0],
  health: {
    battery: 0,
    lowStorage: 0,
    notEncrypted: 0,
    smartStatusFailure: 0,
  },
  topUpdates: [],
});
const appBar = appBarStore();
const alerts = ref([]);
const selectedLicense = ref({});
const loading = ref("Loading");
const selectedTab = ref("dashboard");

function toggleTheme() {
  theme.global.name.value = theme.global.current.value.dark ? "light" : "dark";
}

onMounted(() => {
  theme.global.name.value = "jigsawNew";
});

onMounted(async () => {
  theme.global.name.value = "jigsawNew";
  if (appBar.selected) {
    await getLicense(appBar.selected);
  }
});

appBar.$subscribe(() => {
  console.log("Appbar changed", appBar.$state);
  if (appBar.selected) {
    getLicense(appBar.selected);
    //getDevices();
  }
});

const selectedDrillDownDataType = ref("");
const selectedDrillDownData = ref([]);
const packagesUpdatedType = ref("");

function drillDownSelect(item) {
  if (item.dataType == "updatesPending") {
    selectedDrillDownDataType.value = "updatesPending";
    updateRandom("dashUpdatesPending");
    selectedTab.value = "updatesPending";
    return;
  }
  if (item.dataType == "packagesUpdated") {
    selectedDrillDownDataType.value = "packagesUpdated";
    selectedDrillDownData.value = data.value.topUpdates;
    packagesUpdatedType.value = "Success";
    updateRandom("packagesUpdated");
    selectedTab.value = "packagesUpdated";
    return;
  }
  if (item.dataType == "updatesFailed") {
    selectedDrillDownDataType.value = "updatesFailed";
    selectedDrillDownData.value = data.value.failedUpdates;
    packagesUpdatedType.value = "Failed";
    updateRandom("packagesUpdated");
    selectedTab.value = "packagesUpdated";
    return;
  }
  selectedDrillDownDataType.value = item.dataType;
  updateRandom("dashDrillDown");
  selectedTab.value = "drilldown";
}

async function getAppsList() {
  try {
    const result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "getAppsList",
      licenseId: appBar.selected,
    });
    if (result.length) {
      appsList.value.length = 0;
      appsList.value.push(...result);
    } else {
      alerts.value.push({
        type: "error",
        message: "Error getting apps",
        closable: true,
      });
      console.log("Empty apps list returned");
    }
  } catch (error) {
    alerts.value.push({
      type: "error",
      message: "Error getting apps",
      closable: true,
    });
    console.log("Error getting apps x", error);
  }
}

async function getLicense(id) {
  loading.value = "Loading";

  try {
    var result = await apiQuery("ELA", "patching/patchingManagement", {
      action: "getPatchingLicense",
      id: id,
    });
    if (result.length) {
      selectedLicense.value = result[0];
      await getDashboardData();
    } else {
      alerts.value.push({
        type: "error",
        message: "Error getting license",
        closable: true,
      });
    }
    loading.value = "OK";
  } catch (error) {
    alerts.value.push({
      type: "error",
      message: "Error getting license",
      closable: true,
    });
    loading.value = "Error";
  }
}

async function getDashboardData() {
  try {
    var dashboard;
    var devices;
    getAppsList();
    const patchDashData = await apiQuery("ELA", "patching/patchingManagement", {
      action: "getPatchingDashboard",
      id: selectedLicense.value._id,
    });
    console.log("Patch Dashboard Data", patchDashData);
    if (patchDashData.report) {
      data.value.topRow.forEach((item, index) => {
        switch (item.title) {
          case "Updates Installed":
            item.count = 0;
            patchDashData.patchLogData.installed.forEach((update) => {
              item.count += update.count;
            });
            //item.count = patchDashData.patchLogData.installed.length;
            break;
          case "Updates Failed":
            item.count = 0;
            patchDashData.patchLogData.failed.forEach((update) => {
              item.count += update.count;
            });
            break;
          case "Total Devices":
            item.count = patchDashData.report.totalDevices;
            break;
          case "Updates Pending":
            item.count = patchDashData.report.updatesPending;
            break;
        }
      });
      deviceHealth.forEach((item) => {
        switch (item.title) {
          case "Battery Health":
            item.value = patchDashData.report.poorBatteryHealth;
            break;
          case "Low Storage":
            item.value = patchDashData.report.lowStorage;
            break;
          case "Not Encrypted":
            item.value = patchDashData.report.notEncrypted;
            break;
          case "S.M.A.R.T. Status Failure":
            item.value = patchDashData.report.smartFailing;
            break;
        }
      });
      // data.value.health.battery = patchDashData.report.poorBatteryHealth;
      // data.value.health.lowStorage = patchDashData.report.lowStorage;
      // data.value.health.notEncrypted = patchDashData.report.notEncrypted;
      // data.value.health.smartStatusFailure = patchDashData.report.smartFailing;
      data.value.macsOnline[0] = Math.round(
        (patchDashData.report.devicesOnline /
          patchDashData.report.totalDevices) *
          100
      );
      data.value.topUpdates = patchDashData.patchLogData.installed;
      data.value.failedUpdates = patchDashData.patchLogData.failed;
    }
    // if (selectedLicense.value.mdmServerID) {
    //   // First get dashboard data
    //   const dashboard = await apiQuery("ELA", "cmd24/query", {
    //     dataRequested: "dashboard",
    //     maxage: 86400,
    //     mdmServerId: selectedLicense.value.mdmServerID,
    //   });
    //   dashboardData.value = dashboard;

    //   if (dashboard.content?.macOSOnline?.count) {
    //     data.value.topRow[0].count = dashboard.content.macOSOnline.count;
    //   }
    //   if (dashboard.content?.iosModelReport?.summarisedData) {
    //     let iosCount = 0;
    //     let ipadOSCount = 0;
    //     let keys = Object.keys(dashboard.content.iosModelReport.summarisedData);
    //     keys.forEach((key) => {
    //       if (key.includes("iPad")) {
    //         ipadOSCount +=
    //           dashboard.content.iosModelReport.summarisedData[key].count;
    //       } else {
    //         iosCount +=
    //           dashboard.content.iosModelReport.summarisedData[key].count;
    //       }
    //     });
    //     data.value.topRow[1].count = iosCount;
    //     data.value.topRow[2].count = ipadOSCount;

    //     if (dashboard.content?.macOSOnline?.count) {
    //       // Work out percentage of devices online
    //       data.value.macsOnline[0] = Math.round(
    //         (dashboard.content.macOSOnline.count /
    //           dashboard.content.macOSAll.count) *
    //           100
    //       );
    //     }
    //     if (dashboard.content?.iosOnline?.count) {
    //       // Work out percentage of devices online
    //       data.value.iosOnline[0] = Math.round(
    //         (data.value.topRow[2].count / dashboard.content.iosOnline.count) *
    //           100
    //       );
    //     }
    //   }

    //   // if (dashboard.content?.macOSAll?.records) {
    //   //   const devicesResult = await apiQuery("ELA", "cmd24/query", {
    //   //     dataRequested: "dashboardDetail",
    //   //     id: dashboard.content.macOSAll.records,
    //   //     mdmServerId: selectedLicense.value.mdmServerID,
    //   //   });
    //   //   if (devicesResult.content.length) {
    //   //     devices = [];
    //   //     devices.push(...devicesResult.content);
    //   //   }
    //   // }
    // }
    return {
      devices: devices,
      dashboard: dashboard,
    };
  } catch (error) {
    alerts.value.push({
      type: "error",
      message: "Error getting devices",
      closable: true,
    });
  }
}

let deviceHealth = reactive([
  {
    icon: "mdi-battery",
    title: "Battery Health",
    value: "5",
    dataType: "poorBatteryHealth",
  },
  {
    icon: "mdi-harddisk",
    title: "Low Storage",
    value: "6",
    dataType: "lowStorage",
  },
  {
    icon: "mdi-lock-open-variant-outline",
    title: "Not Encrypted",
    value: "25",
    dataType: "notEncrypted",
  },
  {
    icon: "mdi-harddisk-remove",
    title: "S.M.A.R.T. Status Failure",
    value: "0",
    dataType: "smartFailing",
  },
]);

let demoPieChart = {
  chart: {
    type: "radialBar",
  },
  colors: ["#e7541e"],
  // labels: ["Devices Online"],
  legend: {
    show: false,
    display: false,
  },
  plotOptions: {
    radialBar: {
      hollow: {
        size: "55%",
        margin: 200,
      },
      track: {
        show: true,
        strokeWidth: "100%",
      },
      dataLabels: {
        name: {
          show: false,
        },
        value: {
          show: true,
          offsetY: 6,
          fontWeight: "bold",
          fontSize: "22px",
        },
      },
    },
  },
  grid: {
    show: false,
    padding: {
      top: -20,
      right: 0,
      bottom: -20,
      left: 0,
    },
  },
};
let demoPieChartSeries = [50];

const headersTopUpdates = [
  { title: "App Name", value: "name", sortable: true },
  { title: "Version", value: "version", sortable: true },
  { title: "Install Count", value: "count", sortable: true },
];

const sortBy = ref([{ key: "count", order: "desc" }]); // default sort by 'count' descending

const selectedTopUpdate = ref({});
function topUpdateRowClick(item, row) {
  selectedTopUpdate.value = { ...row.item, type: "installUpdate" };
  updateRandom("logDrillDown");
  selectedTab.value = "logDrillDown";
}

const topUpdates = computed(() => {
  return data.value.topUpdates.map((update) => {
    return {
      appId:
        appsList.value.find((app) => app.name === update.name)?.id || "unknown",
      ...update,
    };
  });
});
</script>

<template>
  <template v-if="!appBar.selected">
    <v-row>
      <v-col>
        <h1 v-if="appBar.items.length > 1">Select Patching License Above</h1>
        <h1 v-else>Loading please wait...</h1>
      </v-col>
    </v-row>
  </template>

  <template v-else>
    <template v-if="loading == 'OK'">
      <v-window v-model="selectedTab">
        <v-window-item value="logDrillDown">
          <div style="display: flex; align-items: center" class="mb-2">
            <BreadcrumbEmit
              label="Dashboard"
              @click="selectedTab = 'dashboard'"
            />
            <BreadcrumbSeperator />
            <BreadcrumbEmit :label="selectedTopUpdate.name" notLink />
          </div>
          <LogDrillDown
            :key="randomKeys.logDrillDown"
            :selected="selectedTopUpdate"
            :license="selectedLicense"
            @Backxxx="
              console.log('Back button dashboard clicked');

              selectedTopUpdate = null;
            "
          />
        </v-window-item>

        <v-window-item value="drilldown">
          <DashDrillDown
            :key="randomKeys.dashDrillDown"
            :license="selectedLicense"
            :dataType="selectedDrillDownDataType"
            @Back="
              selectedTab = 'dashboard';
              selectedDrillDownDataType = null;
            "
          />
        </v-window-item>
        <v-window-item value="packagesUpdated">
          <PackagesUpdated
            :key="randomKeys.packagesUpdated"
            :license="selectedLicense"
            :updates="selectedDrillDownData"
            :type="packagesUpdatedType"
            @Back="selectedTab = 'dashboard'"
          />
        </v-window-item>
        <v-window-item value="updatesPending">
          <DashUpdatesPending
            :key="randomKeys.dashUpdatesPending"
            :license="selectedLicense"
            @Back="selectedTab = 'dashboard'"
          />
        </v-window-item>
        <v-window-item value="dashboard">
          <v-row class="mt-5">
            <v-col cols="3" v-for="row in data.topRow" :key="row">
              <v-card rounded class="" @click="drillDownSelect(row)">
                <v-row>
                  <v-col class="d-flex">
                    <v-sheet class="d-flex align-center pl-5" :height="90">
                      <v-sheet
                        :height="50"
                        :width="50"
                        class="d-flex justify-center align-center pa-0 ma-0"
                        color="#e7541e"
                        rounded
                      >
                        <v-icon :icon="row.icon" size="x-large"></v-icon>
                      </v-sheet>

                      <v-sheet class="d-flex flex-column pl-5">
                        <v-sheet class="font-weight-medium">{{
                          row.title
                        }}</v-sheet>

                        <v-sheet class="font-weight-bold">{{
                          row.count
                        }}</v-sheet>
                      </v-sheet>
                    </v-sheet>
                  </v-col>
                </v-row>
              </v-card>
            </v-col>
          </v-row>
          <v-row class="d-flex flex-row mb-0">
            <v-col cols="6" class="">
              <v-card class="flex" height="100%">
                <div class="text-subtitle-1 font-weight-bold pl-4 pt-4">
                  macOS Devices Online (last 24 hours)
                </div>
                <div class="d-flex justify-center" style="height: 250px">
                  <apexchart
                    class="ma-0 pa-0"
                    :options="demoPieChart"
                    :series="data.macsOnline"
                    height="250"
                    heighzt="115%"
                  >
                  </apexchart>
                </div>
              </v-card>
            </v-col>

            <!-- <v-col cols="4" class="">
          <v-card class="flex" height="">
            <v-card-title class="mb-n7 pb-n5"
              >iOS/iPadOS Devices Online</v-card-title
            >
            <apexchart
              class="ma-0 pa-0"
              :options="demoPieChart"
              :series="data.iosOnline"
              heighzt="115%"
            >
            </apexchart>
          </v-card>
        </v-col> -->

            <v-col cols="6" class="">
              <v-card rounded class="flex" height="100%">
                <div class="text-subtitle-1 font-weight-bold pl-4 pt-4">
                  Device Health
                </div>
                <v-table>
                  <tbody>
                    <tr
                      v-for="item in deviceHealth"
                      :key="item.title"
                      @click="drillDownSelect(item)"
                      class="pointer"
                    >
                      <td class="text-left">
                        <v-sheet class="d-flex">
                          <v-sheet
                            :height="40"
                            :width="40"
                            class="d-flex justify-center align-center pa-0 ma-2"
                            color="#e7541e"
                            rounded
                          >
                            <v-icon size="large">{{ item.icon }}</v-icon>
                          </v-sheet>
                        </v-sheet>
                      </td>
                      <td class="text-left">
                        {{ item.title }}
                      </td>
                      <td class="text-right">
                        {{ item.value }}
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </v-card>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-card rounded height="100%">
                <div class="text-subtitle-1 font-weight-bold pl-4 pt-4">
                  Top Updates (last 24 hours)
                </div>
                <v-data-table
                  :headers="headersTopUpdates"
                  :items="topUpdates"
                  v-model:sort-by="sortBy"
                  :must-sort="true"
                  hide-default-footer
                  @click:row="topUpdateRowClick"
                >
                  <template v-slot:[`item.name`]="{ item }">
                    <v-row align="center">
                      <v-col cols="auto">
                        <AppIcon :appID="item.appId" />
                      </v-col>
                      <v-col cols="auto">
                        {{ item.name }}
                      </v-col>
                    </v-row>
                  </template>
                </v-data-table>
              </v-card>
            </v-col>
          </v-row>
        </v-window-item>
      </v-window>
    </template>

    <template v-else>
      <v-row>
        <v-col>
          <LoadingStatus v-model="loading" title="Dashboard..." />
        </v-col>
      </v-row>
    </template>
  </template>
</template>
